import React, { useEffect } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { useBlocker } from 'react-router-dom';
import { BaseButton, Spinner } from '../base';
import { mdiDownload, mdiOpenInNew } from '@mdi/js';
import Icon from '@mdi/react';
import { getFileTypeInfo, isPdfFile, isImageFile, FileTypeInfo } from '../../utils/file-type-utils';
import * as S from './document-viewer.styles';
import { workerUrl } from './pdf-worker'; // Initialize local PDF.js worker

export interface DocumentViewerProps {
    url: string;
    documentName?: string;
    metadata?: string;
    enableEditing?: boolean;
    loading?: boolean;
    overlays?: any[];
    onOverlayClick?: (key: string) => void;
    onMetadataUpdated?: (metadata: string) => Promise<void>;
    onDocumentUpdated?: (buffer: ArrayBuffer) => Promise<void>;
    onParseDocument?: () => Promise<void>;
}

// Types that were previously in PDF-viewer
export type DocumentMetadata = any; // Simplified for now
export type OverlayMetadata = {
    height: number;
    left: number;
    top: number;
    width: number;
    pageIndex: number;
    key: string;
};

// Router blocking wrapper component - only use this within a data router context (createBrowserRouter)
// For modals or legacy routers, use DocumentViewer directly
const DocumentViewerWithBlocker: React.FC<DocumentViewerProps> = (props) => {
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => 
            !!props.loading && currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        if (blocker && blocker.state === "blocked") {
            if (window.confirm("The document is still loading. If you leave this page, your changes may not be saved. Do you still want to leave the page?")) {
                blocker.proceed();
            } else {
                blocker.reset();
            }
        }
    }, [blocker]);

    return <DocumentViewer {...props} />;
};

// Plain DocumentViewer without router blocking - safe to use in any context (modals, legacy routers, etc.)
export const DocumentViewer: React.FC<DocumentViewerProps> = ({
    url,
    documentName,
    metadata,
    enableEditing,
    loading,
    overlays = [],
    onOverlayClick,
    onMetadataUpdated,
    onDocumentUpdated,
    onParseDocument,
}) => {
    const fileInfo: FileTypeInfo = getFileTypeInfo(url, documentName);

    const handleOpenInNewTab = () => {
        window.open(url, '_blank');
    };

    const handleDownload = () => {
        // Create an anchor element for download
        const link = document.createElement('a');
        link.href = url;
        link.download = documentName || 'document';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderPdfViewer = () => {
        const defaultLayoutPluginInstance = defaultLayoutPlugin({
            sidebarTabs: (defaultTabs) => [
                defaultTabs[0], // Thumbnail tab
                defaultTabs[1], // Bookmark tab
            ],
        });

        return (
            <S.PdfContainer>
                <Worker workerUrl={workerUrl}>
                    <Viewer
                        fileUrl={url}
                        plugins={[defaultLayoutPluginInstance]}
                    />
                </Worker>
            </S.PdfContainer>
        );
    };

    const renderImageViewer = () => {
        return (
            <S.ImageContainer>
                <img 
                    src={url} 
                    alt={documentName || 'Document'} 
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
            </S.ImageContainer>
        );
    };

    
    const renderContent = () => {
        if (isPdfFile(fileInfo)) {
            return renderPdfViewer();
        } else if (isImageFile(fileInfo)) {
            return renderImageViewer();
        } else {
            // For unknown formats, try PDF viewer
            return renderPdfViewer();
        }
    };

    const shouldShowDownloadButtons = !isPdfFile(fileInfo) && !isImageFile(fileInfo);

    return (
        <S.Container>
            {shouldShowDownloadButtons && (
                <div style={{ 
                    position: 'fixed', 
                    bottom: '20px', 
                    left: '50%', 
                    transform: 'translateX(-50%)', 
                    zIndex: 9999, 
                    display: 'flex', 
                    gap: '12px', 
                    background: 'white', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)' 
                }}>
                    <BaseButton
                        variant="primary"
                        onClick={handleDownload}
                    >
                        <Icon path={mdiDownload} size={0.75} />
                        Download
                    </BaseButton>
                    <BaseButton
                        variant="outlined"
                        onClick={handleOpenInNewTab}
                    >
                        <Icon path={mdiOpenInNew} size={0.75} />
                        Open in New Tab
                    </BaseButton>
                </div>
            )}
            {loading && (
                <S.LoadingContainer>
                    <Spinner />
                </S.LoadingContainer>
            )}
            {renderContent()}
        </S.Container>
    );
};

// Export the plain version as default for backward compatibility with modal usage
// Use named export DocumentViewerWithBlocker only in data router contexts (enter-support)
export { DocumentViewerWithBlocker };
export default DocumentViewer;
